import { useCallback, useEffect, useState } from 'react';
import { ENDPOINTS } from '../config/api';
import { useApi } from './useApi';

type Nullable<T> = T | null;

export type MemorySummaryItem = {
  org_id: Nullable<string>;
  station_id: Nullable<string>;
  summary_md: string;
  source_turn_count: number;
  created_at: string;
  updated_at: string;
};

export type DailySummaryItem = MemorySummaryItem & {
  summary_date: string;
};

export type PeriodicSummaryItem = MemorySummaryItem & {
  period_type: 'week' | 'month' | 'quarter' | 'year';
  period_start_date: string;
  period_end_date: string;
};

export type LatestSummaryResponse = {
  daily: DailySummaryItem | null;
  periodic_latest: Partial<Record<'week' | 'month' | 'quarter' | 'year', PeriodicSummaryItem>>;
  org_id?: string | null;
  station_id?: string | null;
};

export type DailySummaryResponse = {
  total: number;
  days: number;
  limit: number;
  offset: number;
  items: DailySummaryItem[];
};

export type PeriodicSummaryResponse = {
  total: number;
  limit: number;
  offset: number;
  items: PeriodicSummaryItem[];
};

export function useMemorySummaryLatest(orgId = '', stationId = '') {
  const { get } = useApi();
  const [data, setData] = useState<LatestSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      const o = orgId.trim();
      const s = stationId.trim();
      if (o) params.org_id = o;
      if (s) params.station_id = s;
      const json = (await get(ENDPOINTS.memorySummaryLatest, params)) as LatestSummaryResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch latest memory summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, orgId, stationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useMemorySummaryDaily(orgId = '', stationId = '', days = 30, page = 0, limit = 20) {
  const { get } = useApi();
  const [data, setData] = useState<DailySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        days: String(days),
        limit: String(limit),
        offset: String(page * limit),
      };
      const o = orgId.trim();
      const s = stationId.trim();
      if (o) params.org_id = o;
      if (s) params.station_id = s;
      const json = (await get(ENDPOINTS.memorySummaryDaily, params)) as DailySummaryResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch daily memory summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, orgId, stationId, days, page, limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useMemorySummaryPeriodic(
  periodType: '' | 'week' | 'month' | 'quarter' | 'year',
  orgId = '',
  stationId = '',
  page = 0,
  limit = 20,
) {
  const { get } = useApi();
  const [data, setData] = useState<PeriodicSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        limit: String(limit),
        offset: String(page * limit),
      };
      if (periodType) params.period_type = periodType;
      const o = orgId.trim();
      const s = stationId.trim();
      if (o) params.org_id = o;
      if (s) params.station_id = s;
      const json = (await get(ENDPOINTS.memorySummaryPeriodic, params)) as PeriodicSummaryResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch periodic memory summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, periodType, orgId, stationId, page, limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

/* ------------------------------------------------------------------ *
 * Hippocampus knowledge documents (reference docs uploaded by users,  *
 * surfaced as a station-scoped list — see backend hippocampus/routes).*
 * ------------------------------------------------------------------ */
export type KnowledgeDocumentItem = {
  id: string;
  org_id: string;
  station_id: string;
  device_id: string | null;
  owner_type: 'station' | 'device';
  filename: string;
  mime_type: string;
  size_bytes: number;
  status: 'uploaded' | 'processing' | 'ready' | 'failed';
  error: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  content?: string;
};

export type HippocampusKnowledgeResponse = {
  org_id: string;
  station_id: string;
  documents: KnowledgeDocumentItem[];
  counts_by_status: Record<string, number>;
  chunk_count: number;
};

export function useHippocampusKnowledge(
  orgId = '',
  stationId = '',
  deviceId: string | null = null,
  includeStationShared = false,
) {
  const { get } = useApi();
  const [data, setData] = useState<HippocampusKnowledgeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      const o = orgId.trim();
      const s = stationId.trim();
      if (o) params.org_id = o;
      if (s) params.station_id = s;
      if (deviceId) params.device_id = deviceId;
      params.include_station_shared = includeStationShared ? 'true' : 'false';
      const json = (await get(ENDPOINTS.hippocampusKnowledge, params)) as {
        org_id: string;
        station_id: string;
        device_id: string | null;
        items: KnowledgeDocumentItem[];
      };
      // memory-service v2 returns `items[]`; map to hook's expected `documents[]` shape.
      const docs: KnowledgeDocumentItem[] = json.items ?? [];
      const counts_by_status: Record<string, number> = {};
      docs.forEach((d) => {
        counts_by_status[d.status] = (counts_by_status[d.status] ?? 0) + 1;
      });
      setData({
        org_id: json.org_id,
        station_id: json.station_id,
        documents: docs,
        counts_by_status,
        chunk_count: 0, // chunk_count not in list response; fetch per-doc if needed
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch hippocampus knowledge');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, orgId, stationId, deviceId, includeStationShared]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

/** neocortex_summaries rollup — direct from memory-service v2, no chat-api proxy. */
export type NeocortexRollupResponse = {
  org_id: string;
  station_id: string;
  station_summary: {
    id: string;
    org_id: string;
    station_id: string;
    device_id: string | null;
    scope_type: string;
    summary_type: string;
    summary_md: string;
    source_count: number;
    period_start: string | null;
    period_end: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  device_summaries: {
    id: string;
    org_id: string;
    station_id: string;
    device_id: string;
    scope_type: string;
    summary_type: string;
    summary_md: string;
    source_count: number;
    period_start: string | null;
    period_end: string | null;
    created_at: string;
    updated_at: string;
  }[];
};

export function useNeocortexRollup(orgId = '', stationId = '') {
  const { get } = useApi();
  const [data, setData] = useState<NeocortexRollupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/api/v2/memory/neocortex/station/${encodeURIComponent(stationId)}/rollup?org_id=${encodeURIComponent(orgId)}`;
      const json = (await get(url)) as NeocortexRollupResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch neocortex rollup');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [get, orgId, stationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useKnowledgeDocumentContent(documentId: string) {
  const { get } = useApi();
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = ENDPOINTS.knowledgeDocument(documentId);
      const json = (await get(endpoint)) as KnowledgeDocumentItem;
      setContent(json.content || null);
    } catch (e) {
      console.error('Failed to fetch knowledge document content:', e);
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, [get, documentId]);

  return { content, loading, fetch };
}
